import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { WorkflowNode } from '@repo/shared-types';

interface GetAnalyticsConfigProps {
  node: WorkflowNode;
  onDataChange: (nodeId: string, data: Record<string, unknown>) => void;
}

const CHANNEL_OPTIONS = [
  { value: 'ALL', label: 'All Channels' },
  { value: 'PSTN', label: 'Phone (PSTN)' },
  { value: 'WEBRTC', label: 'Web (WebRTC)' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'WEB_WIDGET', label: 'Web Widget' },
  { value: 'EMAIL', label: 'Email' },
];

const MODE_OPTIONS = [
  { value: 'calls', label: 'Calls' },
  { value: 'contacts', label: 'Contacts' },
];

export const GetAnalyticsConfig = ({ node, onDataChange }: GetAnalyticsConfigProps) => {
  const data = node.data;

  const update = (field: string, value: unknown) => {
    onDataChange(node.id, { [field]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={(data.label as string) ?? 'Get Analytics'}
          onChange={(e) => update('label', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date From</label>
        <VariableAutocomplete
          value={(data.dateFrom as string) ?? '{{date_offset:0}}'}
          onChange={(v) => update('dateFrom', v)}
          placeholder="e.g. {{date_offset:0}} or {{date_offset:-7}}"
          className="mt-1"
          inputClassName="font-mono"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {
            '{{date_offset:0}} = today, {{date_offset:-1}} = yesterday, {{date_offset:-7}} = 7 days ago'
          }
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date To</label>
        <VariableAutocomplete
          value={(data.dateTo as string) ?? '{{date_offset:0}}'}
          onChange={(v) => update('dateTo', v)}
          placeholder="e.g. {{date_offset:0}}"
          className="mt-1"
          inputClassName="font-mono"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Channel</label>
        <select
          value={(data.channel as string) ?? 'ALL'}
          onChange={(e) => update('channel', e.target.value)}
          className="border-input bg-background focus-visible:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          {CHANNEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Count By</label>
        <select
          value={(data.mode as string) ?? 'calls'}
          onChange={(e) => update('mode', e.target.value)}
          className="border-input bg-background focus-visible:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          {MODE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-muted-foreground mt-1 text-[10px]">
          Contacts counts each contact once per day instead of counting every call.
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={(data.outputKey as string) ?? 'analytics'}
          onChange={(e) => update('outputKey', e.target.value)}
          className="mt-1 font-mono"
          placeholder="analytics"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {'Access metrics as {{analytics.metricKey.count}} downstream'}
        </p>
      </div>
    </div>
  );
};
